// ---------------------------------------------------------------------------
// Types for the Dataset Semantic View Agent API
// ---------------------------------------------------------------------------

export interface ProfileMetadata {
  source: string;
  format: string;
  file_size_bytes: number;
  file_size_mb: number;
  rows: number;
  columns: number;
  profiled_at: string;
  sheets_processed?: string[];
  sheet_details?: { name: string; rows: number; columns: number }[];
}

export interface ColumnProfile {
  column: string;
  dtype: string;
  non_null_count: number;
  null_count: number;
  null_pct: number;
  unique_count: number;
  cardinality_ratio: number;
  sample_values: string[];
}

export interface NumericStats {
  column: string;
  min: number;
  max: number;
  mean: number;
  median: number;
  std_dev: number;
  q25: number;
  q75: number;
  zeros: number;
  negatives: number;
}

export interface StringStats {
  column: string;
  min_length: number;
  max_length: number;
  avg_length: number;
  most_common: string | null;
  most_common_freq: number;
  top_5: Record<string, number>;
}

export interface QualityDimension {
  score: number;
  [key: string]: unknown;
}

export interface QualityAssessment {
  completeness: QualityDimension;
  uniqueness: QualityDimension;
  consistency: QualityDimension;
  overall: QualityDimension;
}

/** Per-sheet profile — subset of DatasetProfile returned for each sheet */
export interface SheetProfile {
  metadata: { source: string; rows: number; columns: number };
  schema: ColumnProfile[];
  numeric_stats: NumericStats[];
  string_stats: StringStats[];
  date_stats: unknown[];
  quality: QualityAssessment;
}

export interface DatasetProfile {
  metadata: ProfileMetadata;
  schema: ColumnProfile[];
  numeric_stats: NumericStats[];
  string_stats: StringStats[];
  date_stats: unknown[];
  quality: QualityAssessment;
  /** Per-sheet profiles keyed by sheet name — present for multi-sheet Excel */
  per_sheet_profiles?: Record<string, SheetProfile>;
}

export interface UploadResponse {
  session_id: string;
  profile: DatasetProfile;
  preview: Record<string, unknown>[];
}

// Multi-sheet Excel response
export interface SheetInfo {
  name: string;
  columns: number;
  column_names: string[];
  sample_rows: number;
}

export interface SheetSelectionResponse {
  session_id: string;
  needs_sheet_selection: true;
  sheets: SheetInfo[];
  file_name: string;
}

// Union: upload can return data or sheet selection prompt
export type UploadResult = UploadResponse | SheetSelectionResponse;

export function isSheetSelection(r: UploadResult): r is SheetSelectionResponse {
  return (
    "needs_sheet_selection" in r &&
    (r as SheetSelectionResponse).needs_sheet_selection === true
  );
}

export interface AutoProfileAnalysis {
  analysis: string;
  message_count: number;
}

export interface ChatResponse {
  response: string;
  message_count: number;
}

export interface ChatEntry {
  role: "user" | "assistant";
  content: string;
}

export interface ProfileQuestion {
  id: string;
  category: string;
  question: string;
  type: "text" | "textarea" | "select";
  required: boolean;
  placeholder?: string;
  options?: string[] | null;
  suggested_answer?: string;
}

export interface ProfileQuestionsResponse {
  questions: ProfileQuestion[];
}

export interface QAFollowupResponse {
  followup_questions: ProfileQuestion[];
  summary: string;
}

export interface SemanticViewResponse {
  semantic_view: unknown;
  glossary_markdown: string;
  er_diagram_markdown: string;
  artifact_paths: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Semantic View Refinement & Validation Types
// ---------------------------------------------------------------------------
export interface KeySuggestionFK {
  column: string;
  references_sheet: string;
  references_column: string;
  confidence: number;
  reasoning: string;
}

export interface KeySuggestionSheet {
  primary_key: string | null;
  pk_confidence: number;
  pk_reasoning: string;
  foreign_keys: KeySuggestionFK[];
}

export interface KeySuggestionsResponse {
  suggestions: Record<string, KeySuggestionSheet>;
}

export interface RefineSemanticViewResponse {
  response: string;
  updated: boolean;
  semantic_view?: unknown;
  glossary_markdown?: string;
  er_diagram_markdown?: string;
}

export interface NamingStandardsRename {
  old: string;
  new: string;
}

export interface NamingStandardsColumnRename {
  entity: string;
  old: string;
  new: string;
}

export interface NamingStandardsReport {
  case_style: string;
  max_length: number | null;
  renamed_entities: NamingStandardsRename[];
  renamed_columns: NamingStandardsColumnRename[];
}

export interface ApplyNamingStandardsResponse {
  applied: boolean;
  report: NamingStandardsReport;
  semantic_view?: unknown;
  glossary_markdown?: string;
  er_diagram_markdown?: string;
}

export interface MetricDefinition {
  name: string;
  entity: string;
  expression: string;
  description?: string;
  aggregation?: string;
  format?: string;
}

export interface AddMetricDefinitionResponse {
  added: boolean;
  metric: MetricDefinition;
  semantic_view?: unknown;
}

export interface DeleteMetricDefinitionResponse {
  deleted: boolean;
  semantic_view?: unknown;
}

export interface ValidationIssue {
  severity: "critical" | "warning" | "info";
  category: string;
  message: string;
  affected_items: string[];
}

export interface ValidationResult {
  readiness_score: number;
  status: "ready" | "needs_review" | "not_ready";
  issues: ValidationIssue[];
  summary: string;
  recommendations: string[];
  dimension_scores?: Record<string, number>;
}

export interface SessionSummary {
  id: string;
  source_name: string;
  source_type: string;
  rows: number;
  columns: number;
  quality_score: number;
  message_count: number;
  has_semantic_view: boolean;
  created_at: string;
}

export interface SessionDetail {
  id: string;
  source_name: string;
  source_type: string;
  profile: DatasetProfile;
  preview: Record<string, unknown>[];
  chat_history: ChatEntry[];
  semantic_view: unknown | null;
  created_at: string;
  approval?: ApprovalState | null;
  /** Wizard step, incl. the secondary-scan gate states — see PendingScanStep. */
  step?: string;
  error?: string | null;
  is_multi_sheet?: boolean;
  sheets?: SheetInfo[];
}

/** Upload step values every scan-gated wizard (Semantic/Curator/Guardian)
 * can report while the FE polls getSession() right after upload(). */
export type PendingScanStep = "pending_scan" | "quarantined" | "error";

export function isPendingScan(
  step: string | undefined,
): step is "pending_scan" {
  return step === "pending_scan";
}

export interface DatabaseConnectionParams {
  // db_type/database (and the other credential fields) are only required
  // for a fresh manual connect; not needed when connection_id is set
  // instead (resolved server-side from a saved connection — see
  // ztd_app's admin/project_db_config.resolve_connect_config).
  db_type?: string;
  host?: string;
  port?: number;
  database?: string;
  schema_name?: string;
  username?: string;
  password?: string;
  auth_type?: string; // 'password' | 'pat'
  account?: string;
  warehouse?: string;
  role?: string;
  table_or_query?: string;
  connection_id?: string;
}

// Response from /data-semantic/db-connect alone (before any table has been
// profiled) — used to show a table picker instead of eagerly profiling
// every discovered table.
export interface DbConnectDiscoverResponse {
  session_id: string;
  source_type: string;
  tables: string[];
  views: string[];
  schema: string;
  step: string;
}

// Response from /data-semantic/add-source — registers an ADDITIONAL source
// database/schema on an existing semantic session, so tables from more than
// one connection can be merged into a single target model (see
// `select_tables`'s qualified `{table, source_id, schema}` entries).
export interface AddSemanticSourceResponse {
  session_id: string;
  source_id: string;
  label: string;
  tables: string[];
  views: string[];
  schema: string;
}

export interface SemanticSourceSummary {
  source_id: string;
  label: string;
  schema?: string | null;
  tables: string[];
  views: string[];
}

export interface ListSemanticSourcesResponse {
  sources: SemanticSourceSummary[];
}

// Response from /data-semantic/list-schema-tables — browsing a different
// schema on an already-connected source (primary or additional).
export interface ListSemanticSchemaTablesResponse {
  schema: string;
  source_id: string;
  tables: string[];
  views: string[];
}

// Response from /data-semantic/schemas/{session_id} — the schema names
// available on an already-connected source, for the FE's schema dropdown.
export interface ListSemanticSchemasResponse {
  source_id: string;
  schemas: string[];
  current_schema?: string | null;
}

// One entry of the qualified `tables` array `select-tables` accepts — a
// plain string still means "primary source, default schema" (unchanged).
export interface SemanticTableSelector {
  table: string;
  source_id?: string | null;
  schema?: string | null;
}

// ---------------------------------------------------------------------------
// Target Model Types
// ---------------------------------------------------------------------------
export interface TargetInfo {
  id: string;
  name: string;
  description: string;
  features: string[];
}

export interface TargetModelResponse {
  target: string;
  schema: string | null;
  artifact_paths: Record<string, string>;
  artifacts: Record<string, string>;
  optimization_hints?: string;
}

export interface DeployModelRequest {
  session_id: string;
  target: string;
  /** Reuse one of the project's saved *target* connections (password
   * resolved server-side) instead of raw fields below. */
  connection_id?: string;
  project_id?: string;
  host?: string;
  port?: number;
  database: string;
  schema_name?: string;
  username?: string;
  password?: string;
  account?: string;
  warehouse?: string;
  role?: string;
  service_name?: string;
}

export interface DeployResult {
  status: string;
  total_statements: number;
  successful: number;
  errors: number;
  details: Array<{ statement: string; status: string; error?: string }>;
}

export interface DeployModelResponse {
  status: string;
  tables: DeployResult;
  views: DeployResult | null;
  deployed_at: string;
}

export interface ReconcileColumnDiff {
  column: string;
  status: "matched" | "missing" | "extra" | "type_mismatch";
  expected_type?: string;
  actual_type?: string;
}

export interface ReconcileTableDiff {
  table: string;
  status: "matched" | "mismatched" | "missing";
  columns: ReconcileColumnDiff[];
}

export interface ReconcileDeploymentRequest {
  session_id: string;
  target: string;
  connection_id?: string;
  project_id?: string;
  schema_name?: string;
  host?: string;
  port?: number;
  database?: string;
  username?: string;
  password?: string;
  account?: string;
  warehouse?: string;
  role?: string;
  service_name?: string;
}

export interface ReconcileDeploymentResponse {
  target: string;
  tables: ReconcileTableDiff[];
  summary: {
    total: number;
    matched: number;
    missing: number;
    mismatched: number;
    in_sync: boolean;
  };
  checked_at: string;
}

// ---------------------------------------------------------------------------
// Documentation Types
// ---------------------------------------------------------------------------
export interface DocumentationResponse {
  markdown: string;
  paths: Record<string, string>;
  formats_available: string[];
}

// ---------------------------------------------------------------------------
// Semantic Intelligence enhancement-surface types (relationship editing,
// snapshots, HITL approval gate, masking suggestions, DDL validation, cost
// estimation, lineage capture, catalog export, NL Q&A).
// ---------------------------------------------------------------------------
export interface SemanticRelationship {
  from_entity: string;
  to_entity: string;
  from_column: string;
  to_column: string;
  cardinality: string;
  confidence: number;
  user_edited?: boolean;
}

export interface UpdateRelationshipsResponse {
  updated: boolean;
  relationships: SemanticRelationship[];
  er_diagram_markdown: string;
}

export interface SnapshotSummary {
  id: string;
  label: string;
  step?: string;
  created_at: string;
}

export interface SnapshotResponse {
  id: string;
  label: string;
  created_at: string;
}

export interface SnapshotListResponse {
  snapshots: SnapshotSummary[];
}

export interface RestoreSnapshotResponse {
  restored: boolean;
  session_id: string;
  step: string | null;
}

export interface FkSuggestion {
  from_entity: string;
  from_column: string;
  to_entity: string;
  to_column: string;
  cardinality: string;
  confidence: number;
  rationale: string;
}

export interface ModelingWarning {
  severity: "error" | "warning" | "info";
  code: string;
  message: string;
  entities: string[];
}

export interface ScdSuggestion {
  entity: string;
  suggested_type: string;
  rationale: string;
}

export interface ModelingInsightsResponse {
  fk_suggestions: FkSuggestion[];
  warnings: ModelingWarning[];
  scd_suggestions: ScdSuggestion[];
}

export interface ImpactAnalysisResponse {
  entity: string;
  column: string | null;
  relationships: SemanticRelationship[];
  metrics: Array<{ name: string; expression: string }>;
  downstream_entities: string[];
  summary: {
    relationship_count: number;
    metric_count: number;
    safe_to_remove: boolean;
  };
}

export interface SnapshotDiffEntityChange {
  name: string;
  changed_fields: string[];
  columns_added: string[];
  columns_removed: string[];
}

export interface SnapshotDiffRelationshipChange {
  from_entity: string;
  from_column: string;
  to_entity: string;
  to_column: string;
  from_cardinality: string | null;
  to_cardinality: string | null;
}

export interface SnapshotDiffResponse {
  from_snapshot: { id: string; label: string };
  to_snapshot: { id: string; label: string };
  diff: {
    entities: {
      added: string[];
      removed: string[];
      modified: SnapshotDiffEntityChange[];
    };
    relationships: {
      added: SemanticRelationship[];
      removed: SemanticRelationship[];
      changed: SnapshotDiffRelationshipChange[];
    };
    metrics: { added: string[]; removed: string[] };
  };
}

export type SemanticLayerExportFormat = "dbt" | "lookml" | "cube";

export interface SemanticLayerExportResponse {
  format: SemanticLayerExportFormat;
  filename: string;
  content: string;
  path: string;
}

export interface ApprovalState {
  status: "pending" | "approved" | "rejected";
  requested_by: string;
  requested_at: string;
  notes: string;
  decided_by: string | null;
  decided_at: string | null;
  target?: string;
  model_fingerprint?: string;
}

export interface ApprovalResponse {
  approval: ApprovalState;
}

export interface MaskingPlanItem {
  entity: string;
  column_name: string;
  sensitivity: string;
  semantic_type: string;
  suggested_technique_id: string;
}

export interface MaskingPlanResponse {
  plan: MaskingPlanItem[];
  column_count: number;
}

export interface ApplyMaskingPlanResponse {
  guardian_config: unknown;
  column_configs: Array<{
    column_name: string;
    technique_id: string;
    options: Record<string, unknown>;
  }>;
}

export interface DdlValidationError {
  statement_index: number;
  snippet: string;
  message: string;
}

export interface DdlValidationResponse {
  target: string;
  valid: boolean;
  statement_count: number;
  errors: DdlValidationError[];
}

export interface CostEstimateTable {
  entity: string;
  estimated_rows: number;
  avg_row_bytes: number;
  estimated_gb: number;
  monthly_storage_usd: number;
  full_scan_query_usd: number;
}

export interface CostEstimateResponse {
  target: string;
  rates_source: string;
  tables: CostEstimateTable[];
  totals: {
    estimated_gb: number;
    monthly_storage_usd: number;
    full_scan_query_usd: number;
  };
}

export interface CaptureLineageResponse {
  captured: boolean;
  edges: number;
  nodes: number;
}

export interface CatalogExportResponse {
  status: string;
  detail?: string;
}

export interface AskQuestionResponse {
  answer: string;
  answered: boolean;
}

export interface LlmAvailabilityResponse {
  available: boolean;
}

// ---------------------------------------------------------------------------
// Project Types
// ---------------------------------------------------------------------------
export interface ProjectSessionSummary {
  id: string;
  source_name: string;
  rows: number;
  columns: number;
  message_count: number;
  has_semantic_view: boolean;
  has_target_model: boolean;
  created_at: string;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  session_ids: string[];
  sessions: ProjectSessionSummary[];
  session_count: number;
  created_at: string;
  updated_at: string;
}

export interface ResumeSessionResponse {
  session_id: string;
  source_name: string;
  source_type: string;
  profile: DatasetProfile | null;
  preview: Record<string, unknown>[];
  chat_history: ChatEntry[];
  semantic_view: SemanticViewResponse | null;
  target_model: TargetModelResponse | null;
  deployment: unknown | null;
  project_id: string;
  step: string; // landing | profile | semantic | target | deployment
}

// ---------------------------------------------------------------------------
// Metadata Catalog Agent Types
// ---------------------------------------------------------------------------
export interface MetadataColumnEntry {
  column_name: string;
  data_type: string;
  /** Native DB column type (e.g. "VARCHAR(255)", "INTEGER", "DATE") when the
   * source is a live database connection. Empty for file uploads — fall
   * back to `data_type` (the pandas dtype string) for display in that case. */
  native_type?: string;
  description: string;
  business_term: string | null;
  sensitivity: "Public" | "Internal" | "Confidential" | "PII";
  domain: string;
  semantic_type: string;
  pii_category: string;
  source: string;
  table_name: string;
  confidence: number;
  evidence: string;
  status: "draft" | "enriched" | "clarified" | "approved" | "published";
  null_pct: number;
  unique_count: number;
  cardinality_ratio: number;
  sample_values: string[];
  /** Full set of distinct values for low-cardinality coded/flag columns
   * (e.g. Gender -> ["M", "F"]). Empty for free-text/PII/high-cardinality
   * columns — see `_compute_distinct_values` in engine.py. */
  distinct_values?: string[];
  /** Deterministic key-constraint detection (real DB constraints when the
   * source is a live database connection, cardinality/name-pattern
   * heuristics otherwise — see `detect_key_constraints` in engine.py). */
  is_primary_key?: boolean;
  key_type?: "declared" | "inferred" | "composite" | null;
  is_foreign_key?: boolean;
  /** "table.column" reference string, or null when not a foreign key. */
  foreign_key_ref?: string | null;
  fk_confidence?: number | null;
  is_unique?: boolean;
  unique_key_type?: "declared" | "inferred" | null;
  needs_clarification?: boolean;
  clarification_question?: string | null;
  stats?: Record<string, unknown>;
  approved_at?: string;
  published_at?: string;
  data_owner?: string | null;
  data_steward?: string | null;
  tags?: string[];
}

export interface MetadataClarification {
  column_name: string;
  columns: string[];
  question: string;
  current_description: string;
  current_business_term: string;
  confidence: number;
  domain: string;
  sensitivity: string;
}

export interface MetadataUploadResponse {
  session_id: string;
  source_name: string;
  source_type: string;
  table_name?: string;
  is_multi_sheet?: boolean;
  sheets?: MetadataSheetInfo[];
  tables?: string[];
  schema_name?: string;
  profile: DatasetProfile;
  preview: Record<string, unknown>[];
  columns_metadata: MetadataColumnEntry[];
  pii_analysis: MetadataPiiAnalysis[];
}

export interface MetadataSheetInfo {
  name: string;
  row_count: number;
  col_count: number;
  columns: string[];
}

export interface MetadataPiiAnalysis {
  column: string;
  dtype: string;
  sensitivity: string;
  category: string;
  suggested_technique: string | null;
  sample_values: string[];
  null_pct: number;
  unique_count: number;
  total_count: number;
}

export interface MetadataEnrichResponse {
  session_id: string;
  columns_metadata: MetadataColumnEntry[];
  clarifications: MetadataClarification[];
  glossary_markdown: string;
  lineage_mermaid: string;
  relationships?: MetadataTableRelationship[];
  dataset_summary?: string;
}

export interface MetadataTableRelationship {
  from_table: string;
  from_column: string;
  to_table: string;
  to_column: string;
  confidence: number;
}

export interface DDLResult {
  ddl: string;
  db_type: string;
  ddl_type: string;
  table_name?: string;
  table_names?: string[];
  schema_name?: string;
  column_count: number;
  tables?: { table_name: string; column_count: number }[];
}

export interface SupportedDatabase {
  id: string;
  label: string;
}

export interface MetadataClarifyRequest {
  session_id: string;
  column_name: string;
  answer: string;
  updated_fields?: {
    description?: string;
    business_term?: string;
    sensitivity?: string;
    domain?: string;
  };
  group_columns?: string[];
}

export interface MetadataClarifyResponse {
  session_id: string;
  column: MetadataColumnEntry;
  updated_columns?: MetadataColumnEntry[];
  remaining_clarifications: number;
}

export interface MetadataApproveResponse {
  session_id: string;
  columns_metadata: MetadataColumnEntry[];
  approved_count: number;
  total_count: number;
}

export interface MetadataPublishResponse {
  session_id: string;
  columns_metadata: MetadataColumnEntry[];
  published_count: number;
  version: number;
  glossary_entries?: GlossaryEntry[];
}

export interface GlossaryEntry {
  business_term: string;
  column_name: string;
  domain: string;
  description: string;
  sensitivity: string;
  data_type: string;
  semantic_type: string;
  pii_category: string;
  data_owner: string;
  data_steward: string;
  tags: string[];
  quality_score: number | null;
  lineage_notes: string;
}

export interface SavedMetadataSession {
  session_id: string;
  source_name: string;
  source_type: string;
  table_name?: string;
  step: string;
  column_count: number;
  created_at: string;
  published_count: number;
  approved_count: number;
}

export interface MetadataExportResponse {
  content?: string;
  path?: string;
}

export interface MetadataWriteBackResponse {
  total: number;
  successful: number;
  errors: number;
  details: { column: string; status: string; error?: string; sql?: string }[];
  connection_error?: string;
}

export interface MetadataVersionEntry {
  version: number;
  timestamp: string;
  action: string;
  column_count: number;
  approved_count: number;
  published_count: number;
  avg_confidence: number;
  checksum: string;
}

export interface MetadataSessionState {
  session_id: string;
  source_name: string;
  source_type: string;
  table_name?: string;
  profile: DatasetProfile;
  columns_metadata: MetadataColumnEntry[];
  clarifications: MetadataClarification[];
  chat_history: ChatEntry[];
  glossary_markdown: string;
  lineage_mermaid: string;
  version_history: MetadataVersionEntry[];
  glossary_mode: string;
  step: string;
  created_at: string;
  error?: string | null;
  is_multi_sheet?: boolean;
  sheets?: string[];
}

export interface MetadataDbConnectParams {
  // Required for a fresh manual connect; not needed when connection_id is
  // set instead (resolved server-side — see ztd_app's
  // admin/project_db_config.resolve_connect_config).
  db_type?: string;
  host?: string;
  port?: number;
  database?: string;
  schema_name?: string;
  username?: string;
  password?: string;
  account?: string;
  warehouse?: string;
  role?: string;
  table?: string;
  connection_id?: string;
}

export interface MetadataDbTablesResponse {
  session_id: string;
  source_type: string;
  tables: string[];
  schema: string;
}

export interface MetadataChatResponse {
  response: string;
  columns_metadata: MetadataColumnEntry[];
  message_count: number;
}

export interface MetadataPublishedCatalogColumn {
  table_name: string;
  column_name: string;
  business_term: string | null;
  domain: string | null;
  semantic_type: string | null;
  sensitivity: string | null;
  description: string | null;
  status: string;
  confidence: number | null;
}

export interface MetadataPublishedCatalog {
  session_id: string;
  source_name: string;
  published_at: string | null;
  dataset_summary: string | null;
  glossary_markdown: string | null;
  lineage_mermaid: string | null;
  columns: MetadataPublishedCatalogColumn[];
}

export interface MetadataCatalogVersion {
  version: number;
  session_id: string;
  source_name: string;
  status: string;
  published_at: string | null;
  glossary_markdown: string | null;
}

export interface MetadataCatalogSearchResult {
  total: number;
  columns: MetadataPublishedCatalogColumn[];
}
